import type { Dictionary } from './fr';

/**
 * Dictionnaire ANGLAIS.
 *
 * Typé sur le dictionnaire français : une clé ajoutée à `fr` sans son équivalent ici fait échouer
 * `tsc`. La traduction ne peut donc pas prendre de retard en silence.
 *
 * Le tutoiement du français devient un « you » neutre : l'anglais n'a pas la distinction, et rien
 * ne doit être forcé pour la reproduire.
 */
export const en: Dictionary = {
  common: {
    back: 'Back',
    scoreSur100: 'out of 100',
    cancel: 'Cancel',
    save: 'Save',
    retry: 'Try again',
    delete: 'Delete',
    ok: 'Got it',
  },

  a11y: {
    close: 'Close',
    removeLanguage: 'Remove language',
    removeBullet: 'Remove bullet',
    removeProject: 'Remove project',
    removeEducation: 'Remove education',
    removeExperience: 'Remove experience',
    removeTag: 'Remove %{tag}',
    useful: 'Helpful',
    notUseful: 'Not helpful',
    listen: 'Listen to this message',
    notifications: 'Notifications',
    settings: 'Settings',
    changePhoto: 'Change profile photo',
    changeSearchTarget: 'Change your search target',
    importPhoto: 'Import a photo',
    score: 'Score: %{score} out of 100',
  },

  nav: {
    carriere: 'Career',
    offers: 'Jobs',
    news: 'News',
    profile: 'Profile',
    aiAction: 'AI action',
  },

  // BLOC CONSERVÉ SANS SON ÉCRAN : l'accueil a été supprimé, mais `HomeContentCard` reste — c'est
  // l'affichage du fil de contenu alternjob.com, destiné à l'onglet News. Le préfixe `home` ne
  // désigne donc plus aucun écran : à renommer en `content` le jour où News recevra ce fil.
  home: {
    content: {
      seeAll: 'See all',
      new: 'NEW',
      duration: '%{minutes} min',
      play: 'Listen',
      types: {
        podcast: 'AlternJob Podcast',
        tip: 'Tip of the day',
        event: 'Not to be missed',
        success: 'They did it',
        market: 'Market and jobs',
        masterclass: 'Masterclass',
        challenge: 'Challenge',
        news: 'New on Horméia',
      },
      sample: {
        title: 'AI and hiring: what’s really changing in 2024',
        description: 'Advice, first-hand stories and job-market trends.',
      },
    },
  },


  carriere: {
    title: 'Career',
    // Coupure PROPRE à l'anglais, pas un calque du français : « Become the candidate » fait déjà
    // une ligne pleine, et « that » l'équilibre en tête de la seconde là où le français descend
    // « que ». Trois lignes décroissantes, la dernière portant le mot au dégradé.
    headline: 'Become the candidate\nthat recruiters',
    headlineAccent: 'call back.',
    position: 'Card %{index} of %{total}',
    cv: {
      title: 'ATS CV',
      description:
        'Build a professional CV, optimised to get through the filters and catch a recruiter’s eye.',
      cta: 'Analyse my CV',
    },
    linkedin: {
      title: 'LinkedIn',
      description: 'Build a professional profile and get yourself seen by recruiters and companies.',
      cta: 'Improve my profile',
    },
    letter: {
      title: 'Cover letter',
      description: 'Write sharp, tailored letters for every single application.',
      cta: 'Write my letter',
    },
    interview: {
      title: 'AI mock interview',
      description: 'Practise until you walk into every interview ready.',
      cta: 'Practise now',
    },
    // Page Carrière sur grand écran : panneau « prochain pas », chiffres et état de chaque outil.
    tableau: {
      prochainPas: 'Your next step to land interviews',
      premierPas: 'Start by knowing where you stand',
      conseilParDefaut: 'Your CV can still improve: open the details to see the fixes.',
      sansScore: 'Upload your CV: you get an ATS score and the fixes that matter, in seconds.',
      corriger: 'Fix my CV',
      noter: 'Score my CV',
      chiffreCv: { one: 'CV saved', other: 'CVs saved' },
      chiffreLettres: { one: 'letter written', other: 'letters written' },
      chiffreCorrections: { one: 'suggested fix', other: 'suggested fixes' },
      statutCvAucun: 'No CV yet',
      statutCv: { one: '%{count} CV', other: '%{count} CVs' },
      statutScore: 'score %{score}',
      statutLettreAucune: 'No letter yet',
      statutLettres: { one: '%{count} letter written', other: '%{count} letters written' },
      statutLinkedin: 'One-minute audit',
      statutInterview: 'Recruiter or manager',
    },
  },

  offers: {
    title: 'Jobs',
    subtitle: 'Soon: openings picked for your profile and your search.',
    soonTitle: 'New feature in the works',
    soonBody:
      'We’re putting together a selection of apprenticeship and entry-level openings that match your CV and what you’re looking for. In the meantime, your CV, your LinkedIn profile and your letters are all waiting in the Career tab.',
  },

  // Jobs, lot 1 (docs/offres/plan.md). Codes come from the `offres-recherche` edge function;
  // only their labels live here.
  offres: {
    franceUniquement: 'Offers only cover jobs in France and are written in French.',
    titre: 'Jobs',
    sousTitre: 'Picked from your criteria',
    credits: { zero: 'No AI application left this month', one: '%{count} AI application left', other: '%{count} AI applications left' },
    segments: { pourToi: 'For you', sauvegardees: 'Saved' },
    modifierCriteres: 'Edit my criteria',
    sources: { france_travail: 'France Travail', lba: 'La bonne alternance', adzuna: 'Jobs by Adzuna' },
    contrats: { alternance: 'Work-study', stage: 'Internship', cdi: 'Permanent', cdd: 'Fixed-term', autre: 'Other contract' },
    contratsEnLigne: { alternance: 'work-study', stage: 'internship', cdi: 'permanent', cdd: 'fixed-term', autre: 'other contract' },
    rythmes: {
      '3j2j': '3 d / 2 d',
      '4j1j': '4 d / 1 d',
      '1sem1sem': '1 wk / 1 wk',
      '2sem2sem': '2 wk / 2 wk',
      '3sem1sem': '3 wk / 1 wk',
      '2j3j': '2 d / 3 d',
    },
    rythmesComplets: {
      '3j2j': '3 days at work, 2 days at school',
      '4j1j': '4 days at work, 1 day at school',
      '1sem1sem': '1 week at work, 1 week at school',
      '2sem2sem': '2 weeks at work, 2 weeks at school',
      '3sem1sem': '3 weeks at work, 1 week at school',
    },
    dureesStage: { '2': '2 months', '3': '3 months', '4': '4 months', '5': '5 months', '6': '6 months' },
    dureesCdd: { lt6: 'Under 6 months', '6a12': '6 to 12 months', '12plus': '1 year or more' },
    rayonKm: '%{km} km',
    preferences: {
      titre: 'Your criteria',
      sousTitre: 'We search based on what you choose here.',
      contratMulti: 'you can pick several',
      peuImporte: 'No preference',
      cdiSeul: 'For a permanent contract, there is nothing else to choose.',
      verifier: 'Check the fields flagged above.',
      metier: 'Target job',
      metierPlaceholder: 'E.g. digital communications officer',
      contrat: 'Contract type',
      ville: 'City',
      villePlaceholder: 'E.g. Lyon',
      rayon: 'Radius',
      rythme: 'Work-study schedule',
      rythmeAide: 'Work / school',
      dureeStage: 'Internship length',
      dureeCdd: 'Fixed-term contract length',
      dureeAide: 'Offers that state a different length are left out.',
      reponsesTitre: 'Key answers, never made up',
      permis: 'Driving licence (B)',
      oui: 'Yes',
      non: 'No',
      aRenseigner: 'To fill in',
      disponibilite: 'Available from',
      disponibilitePlaceholder: 'DD/MM/YYYY',
      voir: 'See my jobs',
      metierRequis: 'Enter the job you’re aiming for.',
      villeRequise: 'Enter your city.',
      villeIntrouvable: 'City not found in France. Check the spelling or choose a French city.',
      villeRetenue: 'City understood: %{commune}. If that’s not right, correct it; otherwise, confirm again.',
      contratRequis: 'Pick at least one contract type.',
      disponibiliteInvalide: 'Write the date as DD/MM/YYYY.',
      erreurEnregistrement: 'Couldn’t save your criteria. Try again.',
      chargement: 'Loading your criteria…',
      retour: 'Back to jobs',
    },
    raisons: {
      contrat: 'Contract: %{valeur}',
      contrat_different: 'Different contract from your search',
      distance: '%{valeur} km from your city',
      distance_loin: '%{valeur} km away, beyond your radius',
      distance_inconnue: 'Workplace not specified',
      distance_rayon: 'Within your %{valeur} km radius',
      rythme: '%{valeur} schedule, as you wanted',
      rythme_different: '%{valeur} schedule, different from yours',
      rythme_absent: 'Work-study schedule not specified',
      metier: 'Job title close to yours: “%{valeur}”',
      metier_partiel: 'Job title partly close: “%{valeur}”',
      metier_rome: 'Same job family as yours (ROME code %{valeur})',
      metier_eloigne: 'Job title far from the one you’re aiming for',
      metier_sans_citation: 'Matches the job you’re aiming for',
      metier_partiel_sans_citation: 'Close to the job you’re aiming for',
      permis_a_renseigner: 'Driving licence required: to fill in',
      permis_manquant: 'Driving licence required',
    },
    carte: {
      pourquoi: 'Why it matches',
      publiee: { one: '%{source} · posted %{jours} d ago', other: '%{source} · posted %{jours} d ago' },
      publieeAujourdhui: '%{source} · posted today',
      voirDetail: 'See details',
      voirOffreComplete: 'See the full ad',
      contratDuree: '%{contrat} · %{duree}',
      dureeMois: { one: '%{count} month', other: '%{count} months' },
      salaire: 'Salary: %{valeur}',
      entrepriseInconnue: 'Company not disclosed',
      lieuDistance: '%{lieu} · %{km} km',
      score: 'Match score: %{score} out of 100',
    },
    actions: {
      passer: 'Skip this job',
      sauvegarder: 'Save this job',
      interesse: 'I’m interested',
      aide: 'Swipe right if interested, left otherwise',
      libelles: { passer: 'Skip', sauvegarder: 'Save', interesse: 'Interested' },
    },
    detail: {
      voirCandidature: 'View my application',
      alerteMineur: 'To check: this offer seems to involve %{motifs}, banned or restricted under 18.',
      motifsMineur: { nuit: 'night work', alcool: 'serving alcohol', hauteur: 'work at height' },
      fermer: 'Close details',
      monMatch: 'Your match',
      description: 'Description',
      extrait: 'Excerpt from the ad',
      extraitAide: 'Adzuna only shares the beginning of the ad. The full text is on their website.',
      voirOffreComplete: 'See the full ad',
      entreprise: 'The company',
      infos: 'Details',
      voirOffre: 'See the original posting',
      preparer: 'Prepare my application',
      source: 'Source: %{source}',
      majLe: 'Updated on %{date}',
      licence: 'Reuse subject to the France Travail licence',
      retiree: 'This job has been withdrawn by the recruiter.',
      logo: '%{entreprise} logo',
      sauvegardee: 'Job saved',
      rubriques: {
        nature_contrat: 'Contract nature',
        type_contrat: 'Contract type',
        duree_travail: 'Working hours',
        experience: 'Experience',
        formations: 'Education',
        competences: 'Skills',
        qualites: 'Soft skills',
        savoirs: 'Tools',
        langues: 'Languages',
        permis: 'Licences',
        salaire: 'Salary',
        secteur: 'Industry',
        qualification: 'Qualification',
        deplacements: 'Travel',
        conditions: 'Working conditions',
        postes: 'Openings',
        accessible_th: 'Open to disabled workers',
        contact: 'Contact',
        agence: 'France Travail agency',
        duree_contrat: 'Contract length',
        debut: 'Start date',
        expiration: 'Open until',
        rome: 'ROME code',
      },
    },
    preparation: {
      parallele: 'IN PARALLEL',
      motsClesTitre: 'KEYWORDS FOUND IN THE OFFER',
      motsClesAutres: '+%{count}',
      motsClesA11y: { one: '1 keyword found: %{liste}', other: '%{count} keywords found: %{liste}' },
      motsClesCouvertsA11y: '%{couverts} of %{total} keywords covered by your CV: %{liste}',
      titre: 'Your AI is preparing your application',
      pret: 'Your application is ready',
      pour: 'For %{entreprise} · %{poste}',
      pourPoste: 'For %{poste}',
      fermer: 'Close preparation',
      terminer: 'Close',
      verifier: 'Check my application',
      reessayer: 'Try again',
      retourOffres: 'Back to job offers',
      pied: 'Nothing is sent without your approval',
      progression: 'Preparing the application',
      etapes: { analyse: 'Job offer analysis', cv: 'CV tailored to the offer', lettre: 'Cover letter', controle: 'Quality check' },
      attente: 'Waiting',
      enCours: 'In progress…',
      echecEtape: 'This step failed',
      motsCles: { zero: 'no keywords', one: '1 keyword', other: '%{count} keywords' },
      questions: { zero: 'no questions', one: '1 question', other: '%{count} questions' },
      analyseFaite: '%{motsCles}, %{questions} found',
      cvFait: { zero: 'No keywords included', one: '1 keyword included', other: '%{count} keywords included' },
      titreAligne: ', title aligned with the offer',
      lettreFaite: '%{count}-word letter',
      controleFait: { zero: 'Nothing to confirm', one: '1 point to confirm', other: '%{count} points to confirm' },
      couverture: 'Keywords covered: %{couverts} of %{total}',
      erreurs: {
        quota: 'You have used all your AI applications this month.',
        cv_manquant: 'You need a main CV to prepare an application. Create or import your CV in Career.',
        offre_retiree: 'This job offer is no longer online: the application can’t be prepared.',
        profil_trop_eloigne: 'This job offer is too far from your CV to write a credible letter.',
        echec: 'A step failed. Try again: completed steps are kept.',
      },
    },
    avant: {
      fermer: 'Close',
      naissanceTitre: 'Your date of birth',
      naissanceRaison: 'It adapts the tone of your letter and warns you if an offer isn’t allowed under 18. You only give it once.',
      naissanceFormat: 'DD/MM/YYYY',
      erreurNaissance: 'Your date of birth couldn’t be saved. Try again.',
      continuer: 'Continue',
      annuler: 'Cancel',
      eloigneeTitre: 'This offer is far from your profile',
      eloigneeTexte: 'Your match is %{score} out of 100. You can still prepare your application: one credit will be used if it completes.',
      prepareQuandMeme: 'Prepare anyway',
      nePasPreparer: 'Don’t prepare',
    },
    relecture: {
      titre: 'Check your application',
      fermer: 'Close the review',
      erreurLecture: 'Your application couldn’t be loaded.',
      reessayer: 'Try again',
      manquants: 'Missing from your CV: %{liste}',
      aConfirmer: 'Check before sending',
      raisons: {
        nombre: 'Figure not in your CV',
        date: 'Date not in your CV',
        competence: 'Skill your CV doesn’t show',
        a_confirmer: 'To confirm',
      },
      exact: 'Yes, that’s right',
      corriger: 'Correct',
      enregistrer: 'Save',
      annuler: 'Cancel',
      champCorrection: 'Your correction',
      confirme: 'Confirmed',
      corrige: 'Corrected',
      erreurCorrection: 'This correction couldn’t be saved. Try again.',
      onglets: { cv: 'CV', lettre: 'Letter', reponses: 'Answers' },
      legende: 'Highlighted: changed by the AI for this offer.',
      formation: 'Education',
      competences: 'Skills',
      langues: 'Languages',
      objet: 'Subject:',
      copierLettre: 'Copy the letter',
      erreurCopie: 'Sharing couldn’t open. Try again.',
      reponses: {
        oui: 'Yes',
        non: 'No',
        date: 'Available on %{date}',
        age: '%{age} years old',
        aRenseigner: 'To fill in',
        aucune: 'No screening question found in this offer.',
      },
      exporterCv: 'Export the CV (PDF)',
      exporterLettre: 'Export the letter (PDF)',
      erreurExport: 'The export failed. Try again.',
      voirOffre: 'View the offer',
      voirOffreComplete: 'See the full ad',
      extrait: 'Prepared from an excerpt of the ad: read the full ad before you send your application.',
    },
    etats: {
      chargement: 'Finding your jobs…',
      erreurTitre: 'Couldn’t load jobs',
      erreurTexte: 'Check your connection, then try again.',
      reessayer: 'Try again',
      videTitre: 'No jobs for these criteria',
      videTexte: 'Widen your radius or try a related job.',
      videTexteDuree: 'Widen your radius, try a related job, or relax the contract length.',
      epuiseTitre: 'You’ve seen every job for now',
      epuiseTexte: 'Come back later, or widen your criteria to see more.',
      sourcePartielle: 'A job source isn’t responding: the list may be incomplete.',
      sauvegardeesVideTitre: 'No saved jobs',
      sauvegardeesVideTexte: 'Tap the bookmark on a job to find it here.',
      retiree: 'Job withdrawn',
      decisionEchec: 'Your choice couldn’t be saved. Check your connection.',
    },
  },

  // Article content itself stays in French whatever the interface language: it comes from the
  // alternjob.com CMS, which only publishes in French. Only the screen's frame is translated.
  news: {
    title: 'News',
    segments: { forYou: 'For you', favorites: 'Saved' },
    verified: 'Verified publisher',
    readingTime: '%{count} min read',
    relative: {
      today: 'Today',
      yesterday: 'Yesterday',
      days: { one: '%{count}d ago', other: '%{count}d ago' },
      weeks: { one: '%{count}w ago', other: '%{count}w ago' },
      months: { one: '%{count}mo ago', other: '%{count}mo ago' },
      years: { one: '%{count}y ago', other: '%{count}y ago' },
    },
    like: {
      add: 'Like this article',
      remove: 'Unlike',
      count: {
        zero: 'No likes',
        one: '%{count} like',
        other: '%{count} likes',
      },
      failed: 'We couldn’t update this like.',
    },
    favorite: {
      add: 'Save article',
      remove: 'Remove from saved',
      added: 'Saved',
      notAdded: 'Not saved',
      failed: 'We couldn’t update this one.',
    },
    errorTitle: 'We couldn’t load the feed',
    errorBody: 'Check your connection, then try again.',
    emptyTitle: 'Nothing here yet',
    emptyBody: 'New articles will show up here as soon as they go live.',
    favoritesEmptyTitle: 'Nothing saved',
    favoritesEmptyBody: 'Tap the bookmark on an article to find it here.',
    favoritesSignedOutTitle: 'Sign in to keep your articles',
    favoritesSignedOutBody: 'Saved articles live with your account and follow you across devices.',
    end: 'You’re all caught up.',
  },

  notifications: {
    welcome: {
      title: 'Welcome to Horméia 👋',
      body: 'Start by generating an ATS-optimised CV, then practise with the AI interviewer. Two steps, and you’ll land more interviews.',
    },
    quantify: {
      title: 'Put numbers on your results',
      body: 'On your CV, replace “managed social media” with “+35% engagement in 3 months”. Recruiters and ATS filters remember numbers.',
    },
    followUp: {
      title: 'Follow up after 7 days',
      body: 'No reply a week after you applied? Send the recruiter a short, polite message. A well-timed follow-up often doubles your chances of hearing back.',
    },
  },

  myCv: {
    title: 'My CV',
    scoreLabel: 'Your ATS score',
    scoreNone: 'Run a first check to see your ATS score.',
    scoreClean: 'Nothing left to fix. Your CV is ready.',
    scoreAdvice: {
      one: '%{count} improvement could still boost your chances.',
      other: '%{count} improvements could still boost your chances.',
    },
    scoreActionFirst: 'Check my CV',
    scoreActionDetail: 'See details',
    createOrImport: 'Create or import',
    importTitle: 'Import my CV',
    importSubtitle: 'PDF or Word',
    createTitle: 'Create a CV',
    createSubtitle: 'From a template',
    improve: 'Improve my CV',
    scoreRowTitle: 'Check my CV',
    scoreRowSubtitle: 'ATS score and priorities',
    optimiseTitle: 'Polish my CV',
    optimiseSubtitle: 'Improve what you have',
    myCvs: 'My CVs',
    savedCount: {
      zero: 'No CV saved yet',
      one: '%{count} CV saved',
      other: '%{count} CVs saved',
    },
    savedListOpen: 'View my saved CVs',
    listSubtitle: 'Find, reopen and manage your CVs',
    priorityLabel: 'Fix this first',
    importShort: 'Import',
    createShort: 'Create a CV',
    cvNote: 'Scored CV',
    changer: 'Change',
  },

  cvList: {
    title: 'My CVs',
    subtitle: 'Find, reopen and manage your saved CVs.',
    loadError: 'We couldn’t load your CVs. Try again.',
    emptyTitle: 'No CV yet.',
    emptyText: 'Let’s build your first ATS-optimised CV. It only takes a few minutes.',
    emptyCta: 'Create my CV',
    primary: 'Main',
    setPrimary: 'Set as main',
    edit: 'Edit',
    preview: 'Preview',
    others: 'My other CVs',
    score: 'ATS score %{score}',
    more: 'More actions',
    deleteTitle: 'Delete this CV?',
    deleteBody: '“%{title}” will be permanently deleted.',
  },

  cvImprove: {
    title: 'Polish my CV',
    subtitle: 'The AI suggests specific changes, and you keep the ones you want.',
    loading: 'The AI is reading your CV…',
    blocked: 'This one is for subscribers only.',
    notFound: 'CV not found. Go back to the check and try again.',
    saveFailed: 'We couldn’t save it.',
    reponseInvalide: 'The response was incomplete. Please try again in a moment.',
    defaultTitle: 'Optimised CV',
    choixQuestion: 'Would you like to add a job ad or description to make your CV a better match for the role?',
    choixDeja: 'You checked your CV against a job ad. Do you want to use it to polish your CV?',
    choixNon: 'No, polish it now',
    choixOui: 'Yes, add a job ad',
    choixUtiliser: 'Use the job ad already added',
    ficheTitre: 'Job ad',
    ficheSousTitre: 'Your CV will be tailored to this role, with nothing made up.',
    ficheChamp: 'SCREENSHOTS, PDF OR TEXT',
    ficheAide: 'Combine them if you like: they’ll be read as one job ad. Up to 5 screenshots and 8 MB, PDF up to 20 pages.',
    optimiser: 'Polish my CV',
    passer: 'Skip',
    ficheUtilisee: 'Tailored to the job ad you provided.',
    // Optimisation par modifications (1.2.0).
    revue: {
      titre: {
        one: '%{count} suggested change',
        other: '%{count} suggested changes',
      },
      aide: 'Accept or decline each change. Nothing in your CV changes until you apply.',
      aideTitre: 'Accept or decline each change.',
      aideSuite: 'Nothing in your CV changes until you apply.',
      // Libellé SEUL, posé à côté du nombre affiché dans sa tuile (le titre complet reste `titre`).
      libelle: { one: 'suggested change', other: 'suggested changes' },
      score: 'The updated CV isn’t scored again: check it afterwards to see its ATS score.',
    },
    tags: { impact: 'Impact', action: 'Action verb', keywords: 'Keywords', clarity: 'Clarity', title: 'Title' },
    modif: { avant: 'Before', apres: 'After', appliquer: 'Apply: %{nom}', titreCv: 'CV headline' },
    pied: {
      retenues: 'Kept: %{count} of %{total}',
      appliquer: {
        one: 'Apply %{count} change',
        other: 'Apply %{count} changes',
      },
      aucune: 'No change selected',
    },
    vide: { titre: 'Your CV is already strong', detail: 'The AI found no useful change to suggest. Nothing was taken from your quota.' },
    scoreInconnu: { titre: 'Score not measured', detail: 'Check this CV to see its ATS score.' },
  },

  ficheDePoste: {
    choisirOffre: 'Pick a job',
    chargement: 'Loading your jobs…',
    erreur: 'We couldn’t load your saved jobs.',
    vide: 'No saved job yet. Keep one in the Offers tab to find it here.',
    coller: 'Paste the full ad',
    compteur: '%{n} / %{max} characters',
    retirerCapture: 'Remove screenshot %{n}',
    retirerFichier: 'Remove the file',
    erreurs: {
      tropDeCaptures: '%{max} screenshots at most.',
      captureIllisible: 'A screenshot couldn’t be read. Pick another image.',
      tropLourd: 'The screenshots go over 8 MB in total. Remove one to continue.',
      pdfTropLong: 'This PDF is over 20 pages. Keep only the job ad.',
    },
  },

  cvScore: {
    failed: 'Scoring failed. Try again.',
    title: 'Check your CV',
    targetRoleLabel: 'Target role',
    targetRoleHint: 'The role you’re targeting: the AI also rates how well your CV fits it.',
    targetRolePlaceholder: 'e.g. Web developer apprenticeship',
    offerLabel: 'Job ad',
    offerHint: 'Add the job ad for a match score and advice targeted at this role.',
    offerPlaceholder: 'Paste the job ad here…',
    offerFile: 'Job ad (PDF/Word)',
    offerCaptures: 'Screenshots',
    optional: 'Optional',
    ficheAdded: 'Added',
    moreAccurate: 'For a more accurate score',
    frise: { import: 'Import', evalue: 'AI reviews', score: 'Your score' },
    dropTitle: 'Drop your CV',
    dropSub: 'PDF or Word. You get your ATS score and concrete advice.',
    dropReady: 'CV ready to be checked',
    dropTextReady: 'Your CV text is ready.',
    dropPick: 'Choose a file',
    dropChange: 'Change file',
    hintEmpty: 'Import a CV to get your ATS score.',
    hintReady: 'Ready: your score arrives in a few seconds.',
    submit: 'Check my CV',
    atsScore: 'ATS score',
    offerMatch: 'Match with the job ad',
    strengths: 'STRENGTHS FOR THIS ROLE',
    gaps: 'GAPS VERSUS THE AD',
    saveImported: 'Save to My CVs',
    saveFailed: 'We couldn’t save it. Try again.',
    importedTitle: 'Imported CV',
    improve: 'Polish my CV',
    verdict: { faible: 'Needs work', correct: 'Decent, room to improve', bon: 'Good CV, a few tweaks', excellent: 'Excellent, ready to send' },
    resultDetail: {
      zero: 'No improvement pending.',
      one: '%{count} improvement could still boost your chances.',
      other: '%{count} improvements could still boost your chances.',
    },
    advicePrio: 'Fix these first',
    rescore: 'Score again',
    rescoreTitre: 'Score again?',
    rescoreMessage: 'You go back to the form. The score shown will be lost.',
    rescoreAnnuler: 'Keep the score',
    criteres: { titre: 'Breakdown by criterion', keywords: 'Keywords', layout: 'ATS layout', impact: 'Quantified results', readability: 'Readability' },
  },

  cvCreate: {
    questions: {
      role: { label: 'Role you’re after', placeholder: 'e.g. Project coordinator' },
      education: {
        label: 'Education, degrees and certifications',
        placeholder: 'e.g. MSc Marketing, IAE Paris',
      },
      experiences: {
        label: 'Work experience (internships, apprenticeships, jobs)',
        placeholder: 'Describe your work experience',
      },
      projects: {
        label: 'Personal projects (personal, volunteering, side projects)',
        placeholder: 'Describe your own projects',
      },
      skills: { label: 'Skills and tools', placeholder: 'e.g. SQL, SEO, Excel, Figma' },
      languages: { label: 'Languages' },
      interests: {
        label: 'Interests and hobbies',
        placeholder: 'e.g. Trail running, photography, chess',
      },
      extra: {
        label: 'Anything to add? (optional)',
        placeholder: 'Free text, the AI will slot it in where it fits',
      },
    },
    steps: {
      analyse: 'Reading your answers',
      format: 'Laying out the CV',
      ats: 'Running the ATS check',
    },
  },

  languagePicker: {
    namePlaceholder: 'Language',
    level: 'Level',
    diploma: 'Certificate (optional)',
    diplomaPlaceholder: 'e.g. TOEFL 105 (or leave blank)',
    add: 'Add a language',
  },

  editors: {
    experience: {
      titre: 'Experience',
      rowTitle: 'Experience %{n}',
      roleLabel: 'Job title',
      end: 'End',
      descriptionLabel: 'What you did',
      descriptionPlaceholder: 'Just write simply, the AI will rephrase it.',
      role: 'Job title',
      company: 'Company',
      start: 'Start',
      current: 'Current role',
      description: 'What you did (the AI will rewrite it)',
      add: 'Add an experience',
    },
    education: {
      titre: 'Education',
      rowTitle: 'Education %{n}',
      degree: 'Degree / title',
      school: 'School',
      date: 'Date',
      current: 'Ongoing',
      add: 'Add a qualification',
    },
    project: {
      titre: 'Projects',
      rowTitle: 'Project %{n}',
      descriptionLabel: 'What you built',
      descriptionPlaceholder: 'Just write simply, the AI will rephrase it.',
      name: 'Project name',
      date: 'Date',
      current: 'Ongoing',
      description: 'What you built (the AI will rewrite it)',
      add: 'Add a project',
    },
    bullets: {
      placeholder: 'Describe a result or an action',
      add: 'Add a bullet',
    },
    template: {
      groupLabel: 'CV template',
      mono: 'Professional',
      accent: 'Colorful',
      accentColor: 'Accent colour',
      couleur: {
        lime: 'Lime green',
        vert: 'Green',
        bleu: 'Blue',
        beige: 'Beige',
        marron: 'Brown',
        ardoise: 'Slate',
        marine: 'Navy',
      },
    },
    quantifyHint: 'Put numbers on your results (how many, what %, what impact). It lifts your ATS score.',
    searchTarget: 'You’re looking for…',
  },

  cvEditor: {
    before: 'BEFORE',
    after: 'AFTER - suggested by the AI',
    reject: 'Reject',
    accept: 'Accept',
    improveWithAi: 'Improve with AI',
    headline: 'CV title',
    experiences: 'Work experience',
    projects: 'Projects',
    education: 'Education',
    skills: 'Skills',
    languages: 'Languages',
    interests: 'Interests',
    headlinePlaceholder: 'e.g. AI Ambassador & Community Coordinator',
    startPlaceholder: 'E.g. Jan. 2024',
    endPlaceholder: 'E.g. Present',
    projectDatePlaceholder: 'E.g. Mar. 2024',
    educationDatePlaceholder: 'E.g. Sep. 2025',
    skillPlaceholder: 'e.g. SQL',
    languagePlaceholder: 'e.g. English - C1',
    interestPlaceholder: 'e.g. Trail running',
    experienceLabel: 'Experience',
    projectLabel: 'Project',
    startLabel: 'Start',
    endLabel: 'End',
    dateLabel: 'Date',
    rewriteRetry: 'Try again.',
  },

  qualityCheck: {
    title: 'What we improved',
    corrections: 'Fixes',
    languagesRemoved: 'Languages removed (below B2)',
    skillsNotProven: 'Skills from the ad you haven’t evidenced',
    toValidate: 'For you to check',
    removed: 'Removed items',
    recommendations: 'Recommendations',
  },

  linkedin: {
    sousScores: { titre: 'Breakdown by section', title: 'Headline', about: 'About', experiences: 'Experience' },
    kit: {
      failed: 'We couldn’t generate your optimisation kit. Your score and priorities are safe.',
      retry: 'Try the kit again',
    },
    title: 'LinkedIn audit',
    subtitle: 'The AI reads your profile and tells you what to improve.',
    verdict: { faible: 'Profile needs work', correct: 'Decent profile, room to improve', bon: 'Good profile, a few tweaks', excellent: 'Excellent profile' },
    resultDetail: {
      zero: 'No priority action.',
      one: '%{count} priority action to improve.',
      other: '%{count} priority actions to improve.',
    },
    profilAnalyse: 'Profile analysed',
    relancer: 'Run again',
    relancerTitre: 'Run the audit again?',
    relancerMessage: 'You will go back to the form. The audit and kit shown now will be lost.',
    relancerAnnuler: 'Keep this audit',
    languageLabel: 'Audit language',
    languageHint: 'Your profile stays in its own language: it’s the audit and the kit that come out in this one.',
    addCaptures: 'Add screenshots',
    analyse: 'Analyse my profile',
    sourceConnect: 'LinkedIn account',
    sourceShots: 'Screenshots',
    capturesGras: 'Up to 5 screenshots',
    capturesSuite: ' of your profile, in the order you want them read.',
    captureRemove: 'Remove screenshot %{n}',
    profil: { titre: 'Headline', apropos: 'About', experiences: 'Experience' },
    promesses: { audit: 'A detailed audit', kit: 'A kit to copy' },
    hint: { empty: 'Add at least one screenshot to start the audit.', ready: 'All set, you can start the audit.', connect: 'Connect your LinkedIn to start the audit.', account: 'Your profile is read straight from your account.' },
    missingKeywords: 'Keywords to add',
    priorities: 'Priority actions',
    connection: {
      secureTitle: 'Secure connection',
      secureBody: 'We only access what’s needed to analyse your profile.',
      connected: 'LinkedIn connected',
      connectedSub: 'Secure connection, no password shared.',
      expired: 'Your LinkedIn session expired. Reconnect to bring it back.',
      unavailable: 'LinkedIn connection is only available in the EU, the EEA and Switzerland.',
      unknown: 'LinkedIn status unavailable right now.',
      failed: 'Couldn’t connect to LinkedIn. Try again.',
      fallbackHint: 'Screenshots still work.',
      eligibilityHint:
        'Available in the EU, the EEA and Switzerland. Elsewhere, use the Screenshots tab — the analysis is identical.',
      connect: 'Connect my LinkedIn',
      reconnect: 'Reconnect',
      disconnect: 'Disconnect',
    },
    optimisation: {
      v1: 'Version 1 (straight to the recruiter)',
      v2: 'Version 2 (keywords)',
      v3: 'Version 3 (stands out)',
    },
  },

  linkedinKit: {
    ready: 'Your kit to copy',
    readyHint: 'Tap “Copy” on a block, or copy the whole kit, then paste it into LinkedIn.',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Failed',
    copyAll: 'Copy the whole kit',
    kitCopied: 'Kit copied',
    kitCopyFailed: 'Copy failed: select the text by hand.',
    copyLabel: 'Copy: %{nom}',
    headline: 'Headline',
    about: 'About section',
    aboutShort: 'Short version',
    aboutFull: 'Full version',
    experiences: 'Key experience',
    skills: 'Skills',
    skillsPinned: 'To pin (5)',
    skillsExtra: 'Additional',
    search: 'Search settings',
    searchTitles: 'Job titles',
    searchContracts: 'Contract types',
    searchLocations: 'Locations',
    searchSectors: 'Sectors',
    searchAvailability: 'Availability',
    express: 'Quick wins',
    photo: 'Photo',
    banner: 'Banner',
    url: 'URL',
    education: 'Education (to paste)',
    quality: 'Quality check',
    keywordsAdded: 'Keywords added',
    languagesRemoved: 'Languages removed (below B2)',
    skillsNotProven: 'Skills not evidenced',
    toValidate: 'For you to check',
    removed: 'Removed items',
    recommendations: 'Recommendations',
    recommended: 'Recommended',
    experienceN: 'Experience %{n}',
    recoAsk: 'Recommendation to ask for',
    recoAskWho: 'Recommendation to ask for (%{who})',
  },

  myLetter: {
    title: 'My cover letter',
    subtitle: 'Write your cover letter, then have it analysed.',
    pitchLead: 'A letter tailored to the role makes all the difference.',
    arguments: {
      analysed: 'Matched to the role you target',
      skills: 'Puts your skills forward',
      structure: 'Convincing structure',
      ready: 'Ready to send',
    },
    createSection: 'Write my letter',
    createTitle: 'Write a letter',
    createSubtitle: 'From one of your CVs',
    listSection: 'My letters',
    listCount: 'My letters · %{count}',
    seeAll: 'See all',
    newLetter: 'New',
    listSubtitle: 'Find, reopen and manage your letters',
    improveSection: 'Improve my letter',
    analyseTitle: 'Analyse my letter',
    analyseSubtitle: 'Score, mistakes found and priorities',
    lastScore: 'Latest score',
    lastScoreNone: 'Run an analysis to see your letter’s score.',
    savedCount: {
      zero: 'No letter saved yet',
      one: '%{count} letter saved',
      other: '%{count} letters saved',
    },
  },

  letterAnalyse: {
    importTitle: 'Import my letter',
    importSubtitle: 'PDF / Word',
    importPicked: 'File imported: %{name}',
    importRemove: 'Remove the file',
    orPaste: 'or paste its text',
    rebuiltFromPdf: 'Text rebuilt from the imported PDF.',
    title: 'Analyse my letter',
    subtitle: 'The AI scores your letter against the role and tells you what to fix first.',
    resultTitre: 'My letter analysis',
    tabSaved: 'My letters',
    tabImport: 'Import',
    tabPaste: 'Paste',
    importHint: 'PDF or Word. It will be saved in My letters with its analysis.',
    pasteLabel: 'Your letter text',
    pastedTitle: 'Pasted letter from %{date}',
    cvCrossedNone: 'No CV: the facts in your letter will not be checked.',
    offerSub: 'Optional, for a more precise analysis',
    optionsTitre: 'More options',
    optionsSousTitre: 'Candidate profile',
    profilRepris: 'Profile taken from your account.',
    sourceSection: 'THE LETTER TO ANALYSE',
    pickSaved: 'Pick a saved letter',
    pickSavedEmpty: 'No saved letters yet',
    pasteTitle: 'Paste a letter',
    pasteSubtitle: 'One you wrote elsewhere',
    pastePlaceholder: 'Paste your letter here…',
    pasteSaved: 'Your pasted letter will be saved to “My letters” along with its analysis.',
    titleLabel: 'Letter title (optional)',
    titlePlaceholder: 'e.g. Communications apprenticeship',
    offerLabel: 'Job ad',
    offerHint: 'Without an ad, the analysis looks at your target role and standard market expectations.',
    offerPlaceholder: 'Paste the job ad here…',
    profileLabel: 'Your profile',
    profileHint: 'It sets the bar: a school student’s letter isn’t judged like an executive’s.',
    metierLabel: 'Target job',
    metierPlaceholder: 'e.g. Communications officer',
    submit: 'Analyse my letter',
    loading: 'The AI is reading your letter…',
    empty: 'Add the letter you want analysed.',
    failed: 'The analysis failed. Try again.',
    cvCrossed: 'Cross-checked against your main CV.',
    cvNotCrossed: 'No CV available: the claims in your letter were not verified.',
    obsolete: 'This letter changed after it was analysed. The score covers the earlier version.',
    reanalyse: 'Run the analysis again',
    improve: 'Improve my letter',
    prioritiesSection: 'Fix these first',
    errorsSection: 'Mistakes found',
    strengthsSection: 'What already works',
    detailSection: 'Criterion by criterion',
    coveredSection: 'REQUIREMENTS COVERED',
    missingSection: 'REQUIREMENTS NOT ADDRESSED',
    incoherenceSection: 'Gaps with your CV',
    faults: {
      zero: 'No mistakes found',
      one: '%{count} mistake found',
      other: '%{count} mistakes found',
    },
    verdicts: {
      a_refaire: 'Start over',
      a_retravailler: 'Needs work',
      envoyable_apres_corrections: 'Sendable after fixes',
      solide: 'Solid',
      excellente: 'Excellent',
    },
    criteres: {
      C1: 'Fit with the role',
      C2: 'Evidence and specifics',
      C3: 'Personalisation',
      C4: 'Structure and length',
      C5: 'Language and style',
      C6: 'Format and etiquette',
    },
    niveaux: {
      insuffisant: 'Insufficient',
      fragile: 'Shaky',
      solide: 'Solid',
      excellent: 'Excellent',
    },
    gravites: {
      majeure: 'Major',
      moyenne: 'Medium',
      mineure: 'Minor',
    },
  },

  // « Améliorer ma lettre » (1.2.0).
  coverLetterImprove: {
    title: 'Improve my letter',
    blocked: 'The improvement could not be completed.',
    retry: 'Try again',
    reponseInvalide: 'The response is incomplete. Try again in a moment.',
    saveFailed: 'Could not save.',
    copieTitre: '%{titre} (improved)',
    tags: { hook: 'Opening', proof: 'Evidence', keywords: 'Keywords', clarity: 'Clarity', closing: 'Closing', subject: 'Subject line' },
    modif: { objet: 'Letter subject line', paragraphe: 'Paragraph %{numero}', appliquer: 'Apply: %{nom}' },
    revue: {
      titre: {
        one: '%{count} suggested change',
        other: '%{count} suggested changes',
      },
      aide: 'Accept or reject each change. Your current letter stays as it is: your choices produce a new letter, added to “My letters”.',
      longueur: 'Letter body: %{avant} words before, %{apres} words after.',
    },
    pied: {
      retenues: 'Kept: %{count} of %{total}',
      appliquer: {
        one: 'Apply %{count} change',
        other: 'Apply %{count} changes',
      },
      aucune: 'No change kept',
    },
    vide: { titre: 'Your letter is already solid', detail: 'The AI found no useful change to suggest. Nothing was deducted from your quota.' },
    fait: { titre: 'New letter saved', detail: 'Your improved letter is in “My letters”. Your original letter has not changed.', ouvrir: 'Open the letter' },
  },

  letterList: {
    open: 'Open',
    title: 'My letters',
    subtitle: 'Find, reopen and manage your saved letters.',
    loadError: 'We couldn’t load your letters. Try again.',
    emptyTitle: 'No letter yet.',
    emptyText: 'Generate your first letter from one of your CVs and it’ll be saved here.',
    emptyCta: 'Write my letter',
    deleteTitle: 'Delete this letter?',
    deleteBody: '“%{title}” will be permanently deleted.',
  },

  letterCreate: {
    erreurFormat: 'The letter couldn’t be written. Try again: no credit was used.',
    modifierAnnonce: 'Edit the job posting',
    change: 'Change',
    changeA11y: 'Change CV',
    optionsTitre: 'More options',
    optionsSousTitre: 'Profile, organization, recipient, email',
    reprisDuCompte: 'Profile and application type taken from your account. What you choose here is remembered for your next letter.',
    failed: 'The letter could not be generated. Try again.',
    annonceOr: 'or import the job ad as a file',
    annonceFile: 'PDF / Word',
    annonceFilePicked: 'Job ad imported: %{name}',
    annonceFileRemove: 'Remove the file',
    title: 'Write a letter',
    subtitle: 'Pick a CV, paste the job ad and set the tone.',
    resultTitle: 'Your letter',
    resultSubtitle: 'Read it over, check what needs checking, then download it.',
    noCvTitle: 'You need a CV first',
    noCvText:
      'Your letter draws on your CV for your experience and your proof. Create or import a CV, then come back here.',
    goToCv: 'Go to My CV',
    toConfirm: 'Check before you send',
    emailBlock: 'Covering email',
    download: 'Download my letter',
    cvUsed: 'CV used',
    annonce: 'Target job ad',
    annoncePlaceholder: 'Paste the job ad here…',
    profil: 'Profile',
    type: 'Type of application',
    ton: 'Letter tone',
    langue: 'Letter language',
    organisation: 'Target organisation',
    organisationPlaceholder: 'e.g. ACME',
    metier: 'Target role',
    metierPlaceholder: 'e.g. Project coordinator',
    recipientName: 'Recipient — name',
    recipientNamePlaceholder: 'Leave blank if you don’t know it',
    recipientRole: 'Recipient — job title',
    recipientRolePlaceholder: 'e.g. Head of recruitment',
    city: 'Your city',
    cityPlaceholder: 'e.g. Lyon',
    addEmail: 'Add a covering email',
    generate: 'Generate my letter',
    hint: 'Pick a CV and add the job ad to start generating.',
    defaultTitle: 'Cover letter',
    saveFailed: 'Your letter is ready but we couldn’t save it.',
    exportFailed: 'The export failed.',
    wordCount: '%{count} words',
    profils: {
      collegien: 'Secondary school (work placement)',
      lyceen: 'Sixth form / high school',
      etudiant_jeune_diplome: 'Student or recent graduate',
      actif_confirme: 'Experienced professional',
      manager_cadre_dirigeant: 'Manager or executive',
      reconversion: 'Career change',
    },
    types: {
      reponse_offre: 'Reply to a job ad',
      candidature_spontanee: 'Speculative application',
      mobilite_interne: 'Internal move',
    },
    tons: {
      classique: 'Classic',
      chaleureux: 'Warm',
      direct: 'Direct',
      entrepreneurial: 'Entrepreneurial',
      institutionnel: 'Institutional',
      executif: 'Executive',
    },
    langues: { francais: 'French', anglais: 'English' },
  },

  interview: {
    startFailed: 'The interview could not start. Try again.',
    noCv: 'You don’t have a CV yet. Create one first to start an interview.',
    title: 'AI interview',
    cvUsed: 'CV used',
    change: 'Change',
    changeA11y: 'Change CV',
    hintEmpty: 'Enter the target role to start the interview.',
    loadError: 'Couldn’t load.',
    addCv: 'Add another CV',
    posteSection: 'Role you’re after',
    posteFile: 'Add a job description',
    postePaste: 'Paste a job ad',
    posteExample: 'e.g. Marketing manager',
    pickFile: 'PDF / Word',
    pickCaptures: 'Screenshots',
    offerPlaceholder: 'Paste the job ad text here…',
    companyType: 'Type of company (optional)',
    companyTypePlaceholder: 'startup, large group, consultancy, SME…',
    adaptNote: 'The AI reads the role and tailors its questions to it.',
    languageLabel: 'Interview language',
    start: 'Start the interview',
    interlocutors: {
      recruiter: {
        label: 'Recruiter',
        points: { motivation: 'Motivation', parcours: 'Background', competences: 'Skills' },
      },
      manager: {
        label: 'Manager',
        points: { terrain: 'Hands-on', metiers: 'The craft', situation: 'Scenarios' },
      },
    },
    quitTitle: 'Give up on this interview?',
    quitBody: 'You’ll lose your progress.',
    quitCancel: 'Keep going',
    quitConfirm: 'Give up',
    muteVoice: 'Mute the recruiter’s voice',
    voixActivee: 'Voice on',
    downloadPdf: 'Download PDF',
    pdfErreur: 'The PDF could not be created. Try again.',
    voixCoupee: 'Voice off',
    voixReviendra: {
      lundi: 'Phone voice for now: the realistic voice will be back on Monday.',
      mois: 'Phone voice for now: the realistic voice will be back on the 1st of the month.',
      entretien: 'Phone voice for the rest of this interview: the realistic voice will be back next time.',
      bientot: 'Phone voice for now: the realistic voice will be back soon.',
    },
    voixRestante: {
      semaine: 'Realistic voice: %{n} more interviews this week.',
      semaineUn: 'Realistic voice: 1 more interview this week.',
      semaineAucun: 'Realistic voice: that’s all for this week. Your phone’s voice takes over until Monday.',
      mois: 'Realistic voice: %{n} more interviews this month.',
      moisUn: 'Realistic voice: 1 more interview this month.',
      moisAucun: 'Realistic voice: that’s all for this month. Your phone’s voice takes over until the 1st.',
    },
    unmuteVoice: 'Turn on the recruiter’s voice',
    listen: 'Listen',
    finish: 'End the interview',
    answerPlaceholder: 'Your answer…',
    stopDictation: 'Stop dictation',
    startDictation: 'Answer out loud',
    clearAnswer: 'Clear',
    send: 'Send',
    strengths: 'Strengths',
    improvements: 'To work on',
    tips: 'Tips',
    restart: 'Start again',
    phasePresentation: 'Introductions',
    phaseOngoing: 'Interview under way',
    feedbackHeader: 'Your feedback',
    overallLabel: 'Overall impression',
    dureeMin: '%{n} min',
  },

  profile: {
    fallbackName: 'Your profile',
    personalInfo: 'Personal details',
    personalInfoSubtitle: 'Manage your details and preferences',
    subscription: 'My subscription',
    subscriptionSubtitle: 'Manage your plan and invoices',
    activity: 'WHAT I’VE DONE THIS MONTH',
    coaching: 'MY COACHING',
    coachingHint: 'The time is agreed by email.',
    bookSession: 'Book a session',
    feedbackTitle: 'Questions, or ideas to make this better?',
    feedbackSubtitle: 'We want to hear it!',
    avatarSheet: 'Avatar photo or background',
    importFailed: 'Import failed. Try again.',
    stats: {
      cvCreated: 'CVs created',
      interviews: 'Mock interviews',
      cvScores: 'CV checks',
      linkedinScores: 'LinkedIn audits',
    },
    noSession: 'No session booked',
    sessionFallback: 'Session',
    lastSession: 'Last session booked on %{date} — %{formule}',
  },

  personalInfo: {
    firstName: 'First name',
    firstNamePlaceholder: 'Your first name',
    lastName: 'Last name',
    lastNamePlaceholder: 'Your last name',
    email: 'Contact email',
    emailPlaceholder: 'you@example.com',
    phone: 'Phone',
    phonePlaceholder: '07 700 900 000',
    address: 'Address / city',
    addressPlaceholder: 'e.g. Paris',
    linkedin: 'LinkedIn URL',
    linkedinPlaceholder: 'https://www.linkedin.com/in/…',
  },

  profileCompletion: {
    title: 'One more thing',
    birthDatePlaceholder: 'DD/MM/YYYY',
    continue: 'Continue',
    later: 'Later',
    saveFailed: 'We couldn’t save it.',
  },

  notifications_screen: {
    title: 'Notifications',
    empty: 'Nothing new for now.',
    settingsSubtitle: 'Your reminders and tips to move your search forward.',
    tips: 'TIPS',
  },

  auth: {
    signup: {
      title: 'Create your account,',
      titleAccent: 'get going.',
      subtitle: 'Improve your CV, prepare your interviews and head for your contract.',
      cta: 'Create my account',
    },
    signupEtablissement: {
      title: 'Create your account',
      titleAccent: 'institution',
      subtitle: 'and give your students a tool to prepare their applications.',
    },
    signupPartenaire: {
      title: 'Create your account',
      titleAccent: 'partner',
      subtitle: 'and give the young people you support a tool to prepare their applications.',
    },
    signin: {
      title: 'Keep',
      titleAccent: 'standing out.',
      subtitle: 'Pick your work back up and head for your next interview.',
      cta: 'Sign in',
    },
    pending:
      'If this address isn’t already in use, you’ll get a confirmation email. Do check your spam folder.',
    invalidEmail: 'Invalid email address.',
    invalidPassword: 'Invalid password.',
    emailLabel: 'Email',
    emailPlaceholder: 'name@email.com',
    passwordLabel: 'Password',
    hidePassword: 'Hide password',
    showPassword: 'Show password',
    tabSignin: 'Sign in',
    tabSignup: 'Create account',
    forgot: {
      link: 'Forgot password?',
      title: 'Get back into',
      titleAccent: 'your account.',
      subtitle: 'Enter your email address and we’ll send you a link to choose a new password.',
      cta: 'Send the link',
      sent: 'If an account exists for this address, an email is on its way with a link to choose a new password. Check your spam folder, and open the link on this phone.',
      back: 'Back to sign in',
    },
    newPassword: {
      title: 'Choose your new',
      titleAccent: 'password.',
      subtitle: 'It replaces your old one.',
      newLabel: 'New password',
      confirmLabel: 'Confirm password',
      tooShort: 'Your password must be at least %{min} characters.',
      mismatch: 'The two passwords don’t match.',
      cta: 'Save password',
      cancel: 'Cancel and sign out',
    },
    or: 'or',
    google: 'Continue with Google',

    // Auth errors. The Supabase-code-to-key mapping lives in src/utils/authErrors.ts.
    error: {
      invalidCredentials: 'Wrong email or password.',
      emailNotConfirmed:
        'Your account isn’t confirmed yet. Open the link we emailed you — do check your spam folder.',
      emailInvalid: 'This email address isn’t valid.',
      validationFailed: 'Check your email and your password.',
      weakPassword: 'That password is too weak. Pick a longer one, with digits and letters.',
      rateLimit: 'Too many attempts. Try again in a few minutes.',
      userBanned: 'This account is suspended. Write to us to find out more.',
      network: 'Can’t connect. Check your internet connection and try again.',
      confirmExpired:
        'This confirmation link is no longer valid. Start creating your account again with the same address to get a new one.',
      confirmWrongDevice: 'Open this link on the phone you created your account on.',
      resetExpired: 'This reset link is no longer valid. Request a new one with “Forgot password?”.',
      resetWrongDevice: 'Open the most recent email, on the phone where you requested the reset.',
      samePassword: 'Choose a password different from your old one.',

      serverSignup:
        'We can’t create your account right now — the problem is on our side, not yours. Try again in a few minutes, or continue with %{providers}.',
      serverSignin:
        'We can’t sign you in right now — the problem is on our side, not yours. Try again in a few minutes, or continue with %{providers}.',
      serverOauth:
        'Sign-in didn’t go through — the problem is on our side, not yours. Try again in a few minutes, or sign in with your email and password.',
      // Neither “it’s on our side” nor “try again in a few minutes”: the browser is what blocked
      // the window, and retrying without changing anything reproduces it exactly.
      popupBlocked:
        'Your browser blocked the Google sign-in window. Allow pop-ups for this site, then start sign-in again — or sign in with your email and password.',
      // The message invents no cause — the code carries none. It stops asserting what is false
      // (“it’s on our side”, “try again”) and points at the two checkable settings.
      appleUnknown:
        'Apple sign-in didn’t go through. Check that you’re signed in to iCloud on this device and that your Apple Account has two-factor authentication. Otherwise, sign in with your email and password.',
      serverSignout: 'Signing out didn’t go through. Try again in a few minutes.',
      serverConfirm:
        'We can’t confirm your account right now — the problem is on our side, not yours. Try again in a few minutes, or continue with %{providers}.',
      serverReset:
        'We can’t reset your password right now — the problem is on our side, not yours. Try again in a few minutes.',

      googleStart: 'We can’t open Google sign-in. Try again in a few minutes.',
      googleResponse: 'Google’s reply was incomplete. Try again.',
      appleResponse: 'Apple’s reply was incomplete. Try again.',
      deleteFailed:
        'Your account was not deleted: the operation failed before anything was removed, your data is intact. Try again in a few minutes.',
      deleteAppleCancelled: 'Deletion cancelled: confirm with Apple to delete your account.',

      providersIos: 'Google or Apple',
      providersAndroid: 'Google',
    },
  },

  onboarding: {
    highlights: { cv: 'CV', linkedin: 'LinkedIn', interview: 'Interviews', coach: 'Coach' },
    title: 'Your profile deserves',
    mots: {
      reponses: 'answers.',
      cv: 'a CV that passes.',
      entretien: 'an interview.',
      contrat: 'a contract.',
    },
    sousTitre: 'We’re with you from your CV all the way to signing your contract.',
    wheel: {
      cv: 'ATS CV',
      linkedin: 'LinkedIn',
      interview: 'AI interviews',
      coach: 'Expert coach',
      offers: 'Jobs',
      letter: 'Letter',
      center: 'Your contract',
    },
    quickSignup: 'Quick signup',
    dataProtected: 'Data protected',
    createAccount: 'Create my account',
    haveAccount: 'I already have an account',
  },


  acceptTerms: {
    titleOutdated: 'Our terms have changed',
    titleFirst: 'Almost there ✨',
    changedPrefix: 'Our ',
    changedTermsLink: 'terms of use',
    changedMiddle: ' and our ',
    changedPrivacyLink: 'privacy policy',
    changedSuffix:
      ' have been updated (version %{version}). Read them, then accept to keep using the app.',
    firstPrefix: 'We’ve simplified our terms to better ',
    firstAccent: 'protect your data',
    pillars: {
      data: 'Data\nprotected',
      transparency: 'Full\ntransparency',
      consent: 'Nothing\nwithout you',
    },
    docTerms: 'Terms of use',
    docPrivacy: 'Privacy policy',
    accept: 'Continue',
    refuseOutdated: 'Decline and sign out',
    refuseFirst: 'Later, sign me out',
    help: 'Need help?',
    deleteAccount: 'Delete my account',
    saveFailed: 'We couldn’t save it. Try again.',
  },

  legalHeaders: {
    documentsEnFrancais: 'Documents available in French',
    privacy: 'Privacy and data',
    terms: 'Terms of use',
    dataDeletion: 'Account deletion',
  },

  plans: {
    free: {
      tagline: 'To get started',
      price: 'Free',
      period: 'forever',
      entretiens: '1 AI interview / month, with a natural recruiter voice',
      features: [
        '1 CV check and 1 CV import / month',
        '1 LinkedIn audit / month',
        '1 cover letter and 1 letter review / month',
        '1 PDF export / month',
        'No AI CV creation',
        'Basic templates',
      ],
      featuresOffres: ['Apprenticeship and job offers: free access', '2 AI applications / month (Offers tab)'],
    },
    plus: {
      tagline: 'The sweet spot',
      period: '/ month',
      entretiens: '8 AI interviews / month',
      voix: '2 interviews a week with a realistic recruiter voice',
      features: [
        '5 CV creations and 5 imports / month',
        '5 CV checks / month',
        '5 LinkedIn audits / month',
        '5 cover letters and 5 letter reviews / month',
        '20 AI section rewrites / month',
        'Unlimited exports and all templates',
      ],
      featuresOffres: ['15 AI applications / month (Offers tab)'],
    },
    pro: {
      tagline: 'To see your search through',
      period: '/ month',
      entretiens: '20 AI interviews / month',
      voix: '5 interviews a week with a realistic recruiter voice',
      ecart: '20 interviews and 60 AI applications a month',
      ecartSansOffres: '20 interviews a month',
      features: [
        'Unlimited: CVs, checks, LinkedIn audits, cover letters and reviews',
        'Unlimited AI rewrites and exports',
        'Premium templates',
      ],
      featuresOffres: ['60 AI applications / month (Offers tab)'],
    },
    school: {
      features: [
        'Every feature, for every student',
        'Built for schools and training centres',
        'Billed per seat',
        'Licence',
        'Institution dashboard',
        'Quarterly report',
        'Customisation',
      ],
    },
  },

  plansExtra: {
    proAnnual: {
      name: 'Pro yearly',
      period: '/year',
      badge: '−%{pourcentage}%',
      equivalent: 'i.e. %{monthly}/month × 12',
      cta: 'Go yearly',
    },
  },

  paywall: {
    mentionAbonnement: 'Monthly or annual subscription depending on the plan chosen, automatically renewed for the same period unless cancelled at least 24 hours before the end of the current period. You can cancel at any time in your App Store or Google Play subscription settings; cancellation takes effect at the end of the current period.',
    tonPlan: 'Your plan',
    bientotSurPlateforme: 'Subscriptions are coming soon to this platform.',
    currentPlan: 'Your current plan: ',
    capReached: 'You’ve run out of uses this month. Your allowance resets on %{date}.',
    notIncluded: 'This feature isn’t included in your current plan.',
    title: 'Step it up',
    tabIndividual: 'Individual',
    tabSchools: 'Schools',
    restore: 'Restore my purchases',
    contact: 'Get in touch',
    recommended: 'Recommended',
    subscribe: 'Subscribe',
    pendingApproval: 'Purchase awaiting approval.',
    prixIndisponibles: 'Prices are unavailable right now. Try again in a moment.',
    genericError: 'Something went wrong.',
    activated: 'Subscription active 🎉',
    restored: 'Purchases restored.',
    activating: 'Purchase received. Activating your subscription…',
    activationEnCours: 'Purchase received. Your subscription is being activated: this can take a moment.',
    restoreEnCours: 'Restore sent. If a subscription is linked to your account, it will appear in a moment.',
    schoolMailSubject: 'Offre établissement Horméia',
  },

  coaching: {
    tarifsIndisponibles: 'Prices temporarily unavailable.',
    reessayer: 'Try again',
    montantIncoherent:
      'Payment is temporarily unavailable: the displayed price doesn’t match the amount to be charged. We’d rather take nothing until that’s fixed.',
    retourSuccesTitre: 'Payment confirmed',
    retourSuccesCorps:
      'Your payment went through. You’ll get a confirmation email with your session details.',
    retourEnCoursTitre: 'Payment being processed',
    retourEnCoursCorps:
      'Your bank hasn’t confirmed the payment yet, which is normal with some payment methods. Don’t start again: you would be charged twice. You’ll get an email as soon as it’s confirmed.',
    retourEchecTitre: 'Payment declined',
    retourEchecCorps:
      'The payment didn’t go through and you were not charged. You can try again, with the same payment method or another one.',
    retourAnnuleTitre: 'Payment cancelled',
    retourAnnuleCorps: 'You interrupted the payment and were not charged. Your slot is still available.',
    retourVersCoaching: 'Back to coaching',
    title: 'Coaching',
    pickSlot: 'Pick my slot',
    soon: 'Coming soon',
    slotBeforePayment: 'Your slot is confirmed before you pay anything.',
    howTitle: 'How it works',
    step1: 'You pick your slot',
    step2: 'You confirm your booking',
    step3: 'You pay for your session',
    pickFormule: 'Choose your format',
    formuleNotFound: 'Format not found. Go back and choose one.',
    reserve: 'Book',
    checkingSlot: 'We’re checking your slot against the calendar…',
    slotConfirmed: 'Your slot is confirmed. All that’s left is payment.',
    chosenFormule: 'CHOSEN FORMAT',
    email: 'EMAIL',
    searching: 'WHAT YOU’RE LOOKING FOR (OPTIONAL)',
      payUnavailable:
        'Payment is temporarily unavailable. Please try again later.',
    paid: 'Payment successful ✓. Your booking is confirmed, Julien will be in touch.',
    processing: 'Your payment is being processed. Don’t pay a second time: you’ll get a confirmation as soon as it’s validated.',
    securePayment: 'Secure card payment (Stripe). You’re charged once, and only once.',
    nameRequired: 'Enter your name.',
    invalidEmail: 'Invalid email address.',
    paymentFailed: 'Payment failed. Try again.',
    paymentFailedShort: 'Payment failed.',
  },

  cvCreateFlow: {
    titre: 'Create a CV',
    etapeSur: '%{n} of %{total}',
    step1Titre: 'Which job are you aiming for?',
    step1Sous: 'The AI tailors the whole CV to this job.',
    step2Titre: 'Your background',
    step2Sous: 'No need to write it well: the AI rephrases for you.',
    step3Titre: 'Last step',
    step3Sous: 'Everything is optional, you can generate your CV now.',
    nameFromProfile: 'From your profile',
    importTitre: 'Already have a CV?',
    importSous: 'Import it, it’s faster.',
    roleRequired: 'Enter the target job to continue.',
    tuileProjet: 'A project',
    tuileAutre: 'Something else',
    optionnel: 'Optional',
    ajoute: 'Added',
    nameLabel: 'Name on the CV',
    namePlaceholder: 'e.g. Jean Dupont',
    languageLabel: 'CV language',
    languageHint: 'You can answer in French and get a CV in English, or the other way round.',
    contratLabel: 'You’re looking for…',
    contratHint: 'Optional. Tap again to clear.',
    contrats: { alternance: 'Work-study', stage: 'Internship', cdi: 'Permanent', cdd: 'Fixed-term' },
    generate: 'Generate my CV',
    next: 'Continue',
    buildingTitle: 'We’re building your CV.',
    buildingHint: 'A few seconds, promise.',
    editTitle: 'Edit my CV',
    cancel: 'Cancel',
    save: 'Save',
    readyTitle: 'Your CV is ready',
    atsScore: 'ATS score',
    edit: 'Edit',
    saving: 'Saving…',
    retrySave: 'Try saving again',
    download: 'Export my CV',
    condensePourUnePage: 'Content condensed to fit on one page',
    aiAdvice: 'ADVICE FROM THE AI',
    targetOffer: 'TARGET A JOB AD (OPTIONAL)',
    offerPlaceholder: 'Paste the job ad here…',
    offerFile: 'Job ad (PDF/Word)',
    offerCaptures: 'Screenshots',
    recommendCta: 'Recommendations for this ad',
    recommendSection: 'RECOMMENDATIONS FOR THIS AD',
    testInScore: 'Test this CV in Check my CV',
    skillPlaceholder: 'e.g. SQL',
    interestPlaceholder: 'e.g. Trail running',
    noterCe: 'Check this CV',
    savedIn: 'Saved in My CVs',
    editsSaved: 'Changes saved',
    saveFailed: 'Your CV is ready but saving it failed.',
    editSaveFailed: 'Saving failed.',
    offerHint: 'Add a job posting: the AI suggests how to showcase your background for this role (without inventing anything). It is up to you to apply it through editing.',
  },

  help: {
    title: 'Help and contact',
    faqSection: 'FREQUENTLY ASKED',
    contactSection: 'CONTACT US',
    writeUs: 'Write to us',
    sendEmail: 'Send an email',
    mailSubject: 'Aide — Horméia',
    faq: {
      ats: {
        q: 'How is my ATS score calculated?',
        a: 'The AI reads your CV against the criteria applicant tracking systems favour: the job’s keywords, a clear structure, standard sections and quantified results. The score reflects that compatibility and comes with concrete advice to raise it.',
      },
      privacy: {
        q: 'Is my data confidential?',
        a: 'Your CVs and details are used to run the app’s features (checks, creation, interviews). A dedicated page on privacy and managing your data is coming soon; until then, write to us with any question.',
      },
      coaching: {
        q: 'How does coaching work?',
        a: 'Coaching is human support from Julien Dauzats, an HR expert — separate from the AI features. You pick a format, book from the app, and the session happens over video.',
      },
    },
  },

  misc: {
    personalInfoTitle: 'Personal details',
    save: 'Save',
    coachNamePlaceholder: 'Your name',
    coachGoalPlaceholder: 'e.g. prepare a data interview next week',
    atsCompatible: 'ATS-friendly',
    completeProfile: 'Complete your profile',
    completeProfileCta: 'Complete my profile',
    later: 'Later',
    feedbackQuestion: 'Was this result useful?',
    send: 'Send',
    importPhoto: 'Upload a photo',
    orColour: 'OR A COLOURED BACKGROUND',
    add: 'Add',
    yearPlaceholder: 'Year',
    comingSoon: 'Coming soon.',
  },

  progress: {
    linkedinKitSections: [
      'Writing your headline…',
      'Writing your About section…',
      'Rewriting your experience…',
      'Picking your skills…',
      'Tuning your search settings…',
      'Preparing your quick wins…',
      'Finalising your kit…',
    ],

     linkedinSections: [
      'Reading your profile…',
      'Spotting the classic mistakes…',
      'Preparing your score and priorities…',
    ],

    elapsed: {
      start: 'Building your CV…',
      mid: 'A few more seconds…',
      long: 'This is taking a bit longer than usual, hang on.',
    },
    letter: {
      start: 'Writing your letter…',
      mid: 'A few more seconds…',
      long: 'This is taking a bit longer than usual, hang on.',
    },
    linkedin: {
      start: 'Reading your profile…',
      mid: 'A few more seconds…',
      long: 'This is taking a bit longer than usual, hang on.',
    },
    improve: {
      start: 'Polishing your CV…',
      mid: 'A few more seconds…',
      long: 'This is taking a bit longer than usual, hang on.',
    },
    improveLetter: {
      start: 'We’re improving your letter…',
      mid: 'A few more seconds…',
      long: 'This is taking a bit longer than usual, hang on.',
    },
    received: 'Received',
    saved: 'Saved',
  },

  // Licence accordée par l'établissement de l'étudiant (lot 4 du back-office). Aucun lien d'achat ici.
  licenceEcole: {
    title: 'School licence',
    loading: 'Loading…',
    loadError: 'Couldn’t load your licence. Try again.',
    retry: 'Try again',
    intro: 'Did your school send you an invitation code? Enter it to activate your licence.',
    codeLabel: 'Invitation code',
    codePlaceholder: 'XXXX-XXXX-XXXX',
    activer: 'Activate my licence',
    activating: 'Activating…',
    voitTitre: 'What your school can see',
    voit: [
      'Your situation (searching, under contract, contract ended) and key dates, entered by your school.',
      'The support actions it has taken for you: calls, meetings, offers sent.',
      'Your activity in the app: the days you use it. It is visible as soon as your licence is activated, since your school provides it.',
    ],
    neVoitPas: 'It never sees the content of your CVs, letters or interviews.',
    active: 'Licence active',
    via: 'Provided by %{nom}',
    employeurTitre: 'Your employer',
    employeurContrat: 'Your school has linked you to %{nom} since %{date}.',
    employeurVoitTitre: 'What your employer would see if you allow it',
    employeurVoit: [
      'Your name, your programme, your school and your contract dates.',
      'Your situation (searching, under contract, contract ended).',
    ],
    employeurNeVoitPas: 'It never sees your email, your school’s support log, your applications, your activity in the app, or the content of your CVs, letters or interviews.',
    employeurConsentTitre: 'Share my status with my employer',
    employeurConsentBody: 'Off by default. You can withdraw it at any time: your contract and licence don’t change. Your school only sees whether you agreed or not.',
    employeurConsentError: 'Couldn’t save your choice. Try again.',
    succes: 'Your %{nom} licence is activated.',
    erreurs: {
      code_invalide: 'This code isn’t valid. Check it or ask your school for a new one.',
      invitation_expiree: 'This invitation has expired. Ask your school for a new one.',
      abonnement_actif: 'You already have an active subscription. Cancel it in your App Store or Google Play settings, then try again.',
      plus_de_sieges: 'Your school has no seat left. Please contact them.',
      organisation_inactive: 'Your school’s licence isn’t active right now. Please contact them.',
      inconnu: 'Couldn’t activate right now. Try again.',
    },
  },

  settings: {
    title: 'Settings',
    sections: {
      account: 'Account',
      preferences: 'Preferences',
      admin: 'Administration',
      danger: 'Danger zone',
    },
    privacy: 'Privacy and data',
    licenceEcole: 'School licence',
    dataDeletion: 'Account and data deletion',
    notifications: 'Notifications',
    adminDashboard: 'Admin dashboard',
    signOut: 'Sign out',
    deleteAccount: 'Delete my account',
    deleting: 'Deleting…',
    deleteFailedTitle: 'Deletion failed',
    deleteConfirmTitle: 'Delete your account?',
    deleteConfirmBody: 'Your account will be deactivated right away and your data permanently deleted after 29 days. Your subscription is not cancelled automatically: cancel it in your App Store or Google Play settings.',
    language: {
      label: 'App language',
      hint: 'Documents you generate keep their own language, the one you pick when you create them.',
      relanceTitre: 'Restart the app?',
      relanceTexte: 'To switch to this language, Horméia needs to restart. Your saved CVs, letters and settings are kept; an interview or unsaved entry in progress will be lost.',
      relanceOk: 'Restart',
    },
  },
  update: {
    banner: {
      title: 'A new version is available',
      body: 'Update Horméia to get the latest improvements.',
      action: 'Update',
      dismiss: 'Later',
      dismissA11y: 'Dismiss this update prompt',
    },
    blocking: {
      title: 'This version is no longer supported',
      body: 'Horméia has moved on since this version was installed, and it can no longer work properly with the service. This isn’t an outage, and there’s nothing to fix on your side.',
      reassurance: 'Your data and documents are safe — you’ll find them as soon as the new version opens.',
      action: 'Install the update',
    },
  },
  quota: {
    resetDate: '1 %{monthYear}',
    capReachedNoUpgrade:
      'You’ve reached your plan’s limit for this feature. Your allowance resets on %{date}.',
    notIncludedNoUpgrade: 'This feature isn’t part of your licence.',
  },
  errors: {
    network: 'Connection failed. Check your connection and try again.',
    server: 'The service is temporarily unavailable. Try again in a moment.',
    aiBadOutput: 'The AI response couldn’t be used. Run the analysis again.',
    profilTropEloigne: 'This job offer is too far from your CV to write a credible letter.',
    invalidRequest: 'Check the information you entered, then try again.',
    ficheTropLongue: 'The job ad is too long: 20 pages or 10,000 characters at most.',
    unauthorized: 'Your session has expired. Please sign in again.',
  },

  nouveautes: {
    titre: 'What’s new in Horméia',
    offresTitre: 'Work-study offers',
    offresTexte: 'Find work-study and job offers that match your profile, then prepare your application with AI.',
    voirOffres: 'See offers',
    voixTitre: 'The recruiter’s voice',
    voixTexte: 'Interview simulations now have a realistic voice. Depending on your plan: 1 interview a month on Free, 2 a week on Accélérateur or École, 5 a week on Pro.',
    languesTitre: 'New languages',
    languesTexte: 'Horméia is now available in Spanish, Portuguese, Chinese and Arabic. You can change the language in Settings.',
    politiqueTitre: 'Privacy policy',
    politiqueTexte: 'Our privacy policy has been updated to describe how we measure use of the app. You can read it at any time in Settings.',
    voirPolitique: 'Read the policy',
    bouton: 'Let’s go',
  },
};
